import { useParams } from 'react-router-dom';

import { useGetAssetByIdQuery } from '@/store/api/assetsApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

import { getMetaValue } from '@/utils/assetMeta';
import { BackButton } from '@/components/common/BackButton';
import { ExternalLink } from 'lucide-react';

export default function AssetDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const assetId = Number(id);

  const {
    data: asset,
    isLoading,
    isError,
  } = useGetAssetByIdQuery(assetId, {
    skip: !assetId,
  });

  if (isLoading) return <div className="p-6">Loading asset details…</div>;
  if (isError || !asset) return <div className="p-6 text-red-500">Failed to load asset</div>;

  const meta = asset.meta ?? [];
  const documents = asset.documents ?? [];

  return (
    <div className="space-y-8 p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between gap-10">
        <div className="flex items-center gap-10">
          <BackButton />
          <div>
            <h1 className="text-3xl font-bold">{asset.assetTitle ?? '—'}</h1>
            <p className="text-muted-foreground">Owned by {asset.currentOwnerName ?? '—'}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Badge variant={asset.isVerified ? 'default' : 'secondary'}>
            {asset.isVerified ? 'Verified' : 'Pending Verification'}
          </Badge>
          {asset.isSoldOut && <Badge variant="destructive">Sold Out</Badge>}
        </div>
      </div>

      {/* BASIC INFO */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <Info label="Asset Type" value={asset.assetType} />
          <Info label="Asset Sub Type" value={asset.assetSubType} />
          <Info label="Ownership Model" value={asset.ownershipModel} />
          <Info label="Created At" value={formatDate(asset.createdAt)} />
        </CardContent>
      </Card>

      {/* LOCATION */}
      <Card>
        <CardHeader>
          <CardTitle>Location</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <Info label="City" value={getMetaValue(meta, 'city')} />
          <Info label="State" value={getMetaValue(meta, 'state')} />
          <Info label="Country" value={getMetaValue(meta, 'country')} />
          <Info label="Pincode" value={getMetaValue(meta, 'pincode')} />
          <Info label="Geo Location" value={getMetaValue(meta, 'geoLocation')} />
        </CardContent>
      </Card>

      {/* PROPERTY DETAILS */}
      <Card>
        <CardHeader>
          <CardTitle>Property Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <Info label="Built-up Area" value={getMetaValue(meta, 'builtupArea')} />
          <Info label="Land Area" value={getMetaValue(meta, 'landArea')} />
          <Info label="Year of Construction" value={getMetaValue(meta, 'yearOfConstruction')} />
          <Info label="Asset Age" value={getMetaValue(meta, 'assetAge')} />
          <Info label="Title Status" value={getMetaValue(meta, 'titleStatus')} />
          <Info label="RERA Number" value={getMetaValue(meta, 'reraRegistrationNumber')} />
        </CardContent>
      </Card>

      {/* FINANCIALS */}
      <Card>
        <CardHeader>
          <CardTitle>Financials</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <Info label="Market Value" value={getMetaValue(meta, 'marketValue')} />
          <Info label="Rental Income (Monthly)" value={getMetaValue(meta, 'rentalIncomeMonthly')} />
          <Info label="Lease Rent / Month" value={getMetaValue(meta, 'leaseRentPerMonth')} />
          <Info
            label="Lease Tenure (Months)"
            value={getMetaValue(meta, 'leaseTenureRemainingMonths')}
          />
          <Info label="Last 3 Year Revenue" value={getMetaValue(meta, 'last3YearRevenue')} />
        </CardContent>
      </Card>

      {/* DESCRIPTION */}
      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 break-all">
          <p>{getMetaValue(meta, 'shortDescription') ?? '—'}</p>
          <Separator />
          <p className="text-muted-foreground break-all">
            {getMetaValue(meta, 'detailedDescription') ?? '—'}
          </p>
        </CardContent>
      </Card>

      {/* DOCUMENTS */}
      <Card>
        <CardContent className="space-y-3">
          {documents.length === 0 && <p className="text-muted-foreground">No documents uploaded</p>}
          <div>
            <CardHeader>
              <CardTitle>Asset Images</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {documents
                .filter((item) => item?.documentType === 'ASSET_IMAGE')
                .map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative border rounded-md overflow-hidden"
                  >
                    <img src={doc.url} alt="Document" className="h-48 w-full object-cover" />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <ExternalLink className="text-white h-6 w-6" />
                    </div>
                  </a>
                ))}
            </div>
          </div>
          <div>
            <CardHeader>
              <CardTitle>Asset Videos</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {documents
                .filter((item) => item?.documentType === 'ASSET_VIDEO')
                .map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative border rounded-md overflow-hidden"
                  >
                    <video
                      src={doc.url}
                      className="h-48 w-full object-cover"
                      muted
                      playsInline
                      controls
                      preload="metadata"
                      onMouseEnter={(e) => e.currentTarget.play()}
                      onMouseLeave={(e) => {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      }}
                    />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <ExternalLink className="text-white h-6 w-6" />
                    </div>
                  </a>
                ))}
            </div>
          </div>
          <div>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {documents
                .filter(
                  (item) =>
                    item?.documentType !== 'ASSET_IMAGE' && item?.documentType !== 'ASSET_VIDEO'
                )
                .map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative border rounded-md overflow-hidden"
                  >
                    <img src={doc.url} alt="Document" className="h-48 w-full object-cover" />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <ExternalLink className="text-white h-6 w-6" />
                    </div>

                    <div className="p-2 text-xs flex justify-between items-center bg-muted">
                      <span className="capitalize"> #{doc.documentType}</span>
                    </div>
                  </a>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function Info({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium">{value ?? '—'}</p>
    </div>
  );
}

function formatDate(date?: string) {
  return date ? new Date(date).toLocaleDateString() : '—';
}
